import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getTicketsURL } from '../../../utils/urls';
import Layout from '../component/layout';

const CustomerTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ticketDialog, setTicketDialog] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);

    const statuses = [
        { label: 'All', value: null },
        { label: 'Open', value: 'open' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Escalated', value: 'escalated' },
        { label: 'Resolved', value: 'resolved' },
        { label: 'Overdue', value: 'overdue' },
        { label: 'Closed', value: 'closed' },
        { label: 'Reopened', value: 'reopened' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    const priorities = [
        { label: 'All', value: null },
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
        { label: 'Urgent', value: 'urgent' },
        { label: 'Critical', value: 'critical' }
    ];

    useEffect(() => {
        loadTickets();
    }, []);

    const loadTickets = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getTicketsURL, 'GET');

            console.log('Tickets response:', response);

            if (response.success && response.data) {
                // Handle nested response structure - makeRequest2 wraps response
                // Backend returns { success, data: [...] }
                // makeRequest2 wraps it as { success, data: { success, data: [...] } }
                let ticketsData;
                if (response.data.data) {
                    ticketsData = response.data.data;
                } else if (Array.isArray(response.data)) {
                    ticketsData = response.data;
                } else {
                    ticketsData = [];
                }

                setTickets(Array.isArray(ticketsData) ? ticketsData : []);
            } else {
                toastify(response.error || 'Failed to load tickets', 'error');
                setTickets([]);
            }
        } catch (error) {
            console.error('Error loading tickets:', error);
            toastify(error.message || 'Error loading tickets', 'error');
            setTickets([]);
        }
        setLoading(false);
    };

    const viewTicket = (ticket) => {
        setSelectedTicket(ticket);
        setTicketDialog(true);
    };

    const hideDialog = () => {
        setTicketDialog(false);
        setSelectedTicket(null);
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.status} severity={getStatusSeverity(rowData.status)}></Tag>;
    };

    const priorityBodyTemplate = (rowData) => {
        return <Tag value={rowData.priority} severity={getPrioritySeverity(rowData.priority)}></Tag>;
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'open':
                return 'info';
            case 'in_progress':
                return 'warning';
            case 'escalated':
                return 'danger';
            case 'resolved':
                return 'success';
            case 'overdue':
                return 'danger';
            case 'closed':
                return 'success';
            case 'reopened':
                return 'warning';
            case 'cancelled':
                return null;
            default:
                return null;
        }
    };

    const getPrioritySeverity = (priority) => {
        switch (priority) {
            case 'low':
                return 'info';
            case 'medium':
                return 'warning';
            case 'high':
                return 'danger';
            case 'urgent':
                return 'danger';
            case 'critical':
                return 'danger';
            default:
                return null;
        }
    };

    const customerNameBodyTemplate = (rowData) => {
        const customer = rowData.customer_id;
        if (!customer) return 'N/A';

        // Try fullName first, then firstName + lastName
        if (customer.fullName && customer.fullName !== customer.email) {
            return customer.fullName;
        }

        const firstName = customer.firstName || '';
        const lastName = customer.lastName || '';
        const name = `${firstName} ${lastName}`.trim();

        return name || 'N/A';
    };

    const agentNameBodyTemplate = (rowData) => {
        const agent = rowData.assigned_to || rowData.assigned_agent_id;
        return `${agent?.firstName || agent?.fullName?.split(' ')[0] || ''} ${agent?.lastName || agent?.fullName?.split(' ')[1] || ''}`;
    };

    const createdAtBodyTemplate = (rowData) => {
        if (!rowData.createdAt && !rowData.created_at) return 'N/A';

        const dateValue = rowData.createdAt || rowData.created_at;
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return 'Invalid Date';

            return (
                <div>
                    <div>{date.toLocaleDateString()}</div>
                    <small className="text-muted">{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                </div>
            );
        } catch (e) {
            return 'Invalid Date';
        }
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-info p-button-text"
                    onClick={() => viewTicket(rowData)}
                    tooltip="View Details"
                />
            </div>
        );
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/core/dashboard"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item" aria-current="page">
                                    Customer Tickets
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-12 col-md-6 col-lg-3">
                    <div className="card bg-light-primary">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-2">Total Tickets</p>
                                    <h4 className="mb-0">{tickets.length}</h4>
                                </div>
                                <div className="avatar-sm">
                                    <span className="avatar-title bg-primary rounded">
                                        <i className="ti ti-ticket text-white fs-4"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                    <div className="card bg-light-success">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-2">Resolved</p>
                                    <h4 className="mb-0">{tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}</h4>
                                </div>
                                <div className="avatar-sm">
                                    <span className="avatar-title bg-success rounded">
                                        <i className="ti ti-check text-white fs-4"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                    <div className="card bg-light-warning">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-2">In Progress</p>
                                    <h4 className="mb-0">{tickets.filter(t => t.status === 'in_progress').length}</h4>
                                </div>
                                <div className="avatar-sm">
                                    <span className="avatar-title bg-warning rounded">
                                        <i className="ti ti-clock text-white fs-4"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                    <div className="card bg-light-info">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-2">Open</p>
                                    <h4 className="mb-0">{tickets.filter(t => t.status === 'open').length}</h4>
                                </div>
                                <div className="avatar-sm">
                                    <span className="avatar-title bg-info rounded">
                                        <i className="ti ti-inbox text-white fs-4"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5>Customer Tickets</h5>
                                <div className="d-flex gap-2">
                                    <Dropdown
                                        value={statusFilter}
                                        options={statuses}
                                        onChange={(e) => setStatusFilter(e.value)}
                                        placeholder="Filter by Status"
                                        showClear
                                    />
                                    <Dropdown
                                        value={priorityFilter}
                                        options={priorities}
                                        onChange={(e) => setPriorityFilter(e.value)}
                                        placeholder="Filter by Priority"
                                        showClear
                                    />
                                    <InputText
                                        type="search"
                                        value={globalFilter}
                                        onChange={(e) => setGlobalFilter(e.target.value)}
                                        placeholder="Search tickets..."
                                        className="p-inputtext-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <DataTable
                                value={tickets}
                                loading={loading}
                                dataKey="_id"
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                className="datatable-responsive"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} tickets"
                                emptyMessage="No tickets found."
                                globalFilter={globalFilter}
                                filters={{
                                    status: { value: statusFilter, matchMode: 'equals' },
                                    priority: { value: priorityFilter, matchMode: 'equals' }
                                }}
                                scrollable={false}
                                responsiveLayout="scroll"
                            >
                                <Column field="ticket_number" header="Ticket #" sortable style={{ width: '10%' }} />
                                <Column field="title" header="Title" sortable style={{ width: '20%' }} />
                                <Column field="customer_name" header="Customer" body={customerNameBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column field="assigned_to" header="Assigned To" body={agentNameBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '10%' }} />
                                <Column field="priority" header="Priority" body={priorityBodyTemplate} sortable style={{ width: '8%' }} />
                                <Column field="createdAt" header="Created" body={createdAtBodyTemplate} sortable style={{ width: '15%' }} />
                                <Column body={actionBodyTemplate} header="Actions" style={{ width: '8%' }} />
                            </DataTable>

                            <Dialog
                                visible={ticketDialog}
                                style={{ width: '70vw' }}
                                header="Ticket Details"
                                modal
                                className="p-fluid"
                                onHide={hideDialog}
                            >
                                {selectedTicket && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h6 className="text-primary">Ticket Information</h6>
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Ticket #:</strong></td>
                                                        <td>{selectedTicket.ticket_number}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Title:</strong></td>
                                                        <td>{selectedTicket.title}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Status:</strong></td>
                                                        <td><Tag value={selectedTicket.status} severity={getStatusSeverity(selectedTicket.status)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Priority:</strong></td>
                                                        <td><Tag value={selectedTicket.priority || selectedTicket.category_id?.priority || 'N/A'} severity={getPrioritySeverity(selectedTicket.priority || selectedTicket.category_id?.priority)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Category:</strong></td>
                                                        <td>{selectedTicket.category_id?.name || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Created:</strong></td>
                                                        <td>{createdAtBodyTemplate(selectedTicket)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            <h6 className="text-primary mt-3">Description</h6>
                                            <p className="p-3 bg-light rounded">{selectedTicket.description || 'No description provided'}</p>
                                        </div>

                                        <div className="col-md-6">
                                            <h6 className="text-primary">Customer Information</h6>
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Name:</strong></td>
                                                        <td>{selectedTicket.customer_id?.fullName || `${selectedTicket.customer_id?.firstName || ''} ${selectedTicket.customer_id?.lastName || ''}`.trim() || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Email:</strong></td>
                                                        <td>{selectedTicket.customer_id?.email || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Phone:</strong></td>
                                                        <td>{selectedTicket.customer_id?.phoneNumber || selectedTicket.customer_id?.phone || 'N/A'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            <h6 className="text-primary mt-3">Assignment Information</h6>
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Assigned To:</strong></td>
                                                        <td>{(selectedTicket.assigned_agent_id || selectedTicket.assigned_to)?.fullName || agentNameBodyTemplate(selectedTicket) || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Agent Work ID:</strong></td>
                                                        <td>
                                                            {(() => {
                                                                const agent = selectedTicket.assigned_agent_id || selectedTicket.assigned_to;
                                                                if (!agent) return 'N/A';

                                                                // Check both possible field names
                                                                const workId = agent.agent_id || agent.agentId;
                                                                console.log('Agent object for work ID:', {
                                                                    agent,
                                                                    agent_id: agent.agent_id,
                                                                    agentId: agent.agentId,
                                                                    workId
                                                                });

                                                                return workId || 'Not Assigned';
                                                            })()}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </Dialog>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CustomerTickets;